import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const S = '/screenshots/';
const NAVY = '#0d1b3e';
const GOLD = '#c9a227';

const NAV_LINKS = ['Features', 'Integrations', 'Grading', 'Collections', 'Pricing'];

// Hero stats now pull LIVE from /api/public/stats (cached 1h server-side) so they
// stay accurate and climb on their own. These are the fallback values (last known,
// 2026-10-01) shown if the fetch fails, the page never renders blank.
// Rounded-DOWN so the fallback (shown to crawlers + on first paint, before the
// live /public/stats fetch resolves) is always TRUE, never an overstatement.
// Live as of 2026-10-09: 25,904 cards · $447,240 revenue · 7,238 orders.
const STATS_FALLBACK = [
  { key: 'cardsTracked',    value: '25,500+', num: 25500,  label: 'Cards tracked',    fmt: 'count' },
  { key: 'revenueLogged',   value: '$445K+',  num: 445000, label: 'Revenue logged',   fmt: 'money' },
  { key: 'ordersProcessed', value: '7,200+',  num: 7200,   label: 'Orders processed', fmt: 'count' },
];
// Round DOWN to 3 significant figures (under-promise) + "+", comma-formatted.
// 3 sig figs (not 2) so real growth is visible, at 2 figs the hero sat frozen
// at "22,000+" / "7,200+" for thousands of cards even though the live count climbs.
const roundDownSig = (n, sig = 3) => {
  if (!(n > 0)) return 0;
  if (n < 100) return Math.floor(n);
  const mag = Math.pow(10, Math.floor(Math.log10(n)) - (sig - 1));
  return Math.floor(n / mag) * mag;
};
const fmtCount = (n) => roundDownSig(n).toLocaleString() + '+';
const fmtMoney = (n) => {
  if (n >= 1e6) return '$' + (Math.floor(n / 1e5) / 10).toLocaleString() + 'M+';
  if (n >= 1e3) return '$' + Math.floor(n / 1e3).toLocaleString() + 'K+';
  return '$' + Math.floor(n || 0).toLocaleString() + '+';
};

// Rolling count-up for the hero stats, animates 0 -> target the first time the
// number scrolls into view (or on load, since the hero stats sit near the top).
function CountUp({ num, fmt, duration = 1500 }) {
  const ref = useRef(null);
  const animated = useRef(false);
  const rafRef = useRef(0);
  const [display, setDisplay] = useState(() => (fmt === 'money' ? fmtMoney(num) : fmtCount(num)));
  useEffect(() => {
    const fmtVal = (v) => (fmt === 'money' ? fmtMoney(v) : fmtCount(v));
    // Live stats land AFTER the initial count-up has already run (the fetch in
    // the parent resolves post-mount). When that new `num` arrives, snap the
    // display to it. CRITICAL: cancel any in-flight animation frame first — the
    // initial 0->fallback count-up closes over the OLD num, and if its rAF loop
    // is still running when the live value snaps in, it overwrites the live value
    // right back to the fallback as it finishes. That race is why the hero stayed
    // frozen on the fallback for everyone even though the fetch succeeded.
    cancelAnimationFrame(rafRef.current);
    if (animated.current) { setDisplay(fmtVal(num)); return undefined; }
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      animated.current = true;
      obs.disconnect();
      const start = performance.now();
      const step = (t) => {
        const p = Math.min(1, (t - start) / duration);
        const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
        setDisplay(fmtVal(num * eased));
        if (p < 1) rafRef.current = requestAnimationFrame(step);
      };
      rafRef.current = requestAnimationFrame(step);
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => { obs.disconnect(); cancelAnimationFrame(rafRef.current); };
  }, [num, fmt, duration]);
  return <span ref={ref}>{display}</span>;
}

const AI_FEATURES = [
  { icon: 'camera', title: 'AI Card Scanner', desc: 'Take a photo, front and back. AI fills every field: player, year, brand, set, number, parallel. You just hit save.', badge: 'Live' },
  { icon: 'edit', title: 'AI Listing Descriptions', desc: "Hit bulk generate on a stack of 50 cards and you're done before dinner. No more Sunday nights writing listing descriptions." },
  { icon: 'price', title: 'Real-Time Market Pricing', desc: "Pulls live CardHedger comps so you're not guessing. See what the card actually sold for recently.", badge: 'Live' },
  { icon: 'grade', title: '"Should I Grade This?"', desc: 'Photo → AI identifies card → raw market value → exact profit at PSA 10 vs 9 vs 8. Before you spend $30 to find out the hard way.', badge: 'Live' },
];

// Positioned as a constellation around the central SCN mark (x/y are % within
// the .lp-web box). desc is used as the hover tooltip.
const PLATFORMS = [
  { name: 'eBay', logo: '/logos/ebay.svg', x: 50, y: 11, desc: 'Import listings, orders, and purchases. Orders sync every 2 minutes. Bulk list, relist, delist from one screen.' },
  { name: 'Shopify', logo: '/logos/shopify.svg', x: 81, y: 30, desc: 'Your whole inventory live on your Shopify store. Sales flow back without you touching anything. Link Shopify to Whatnot and the same cards sell on Whatnot too.' },
  { name: 'MySlabs', logo: '/logos/myslabs.png', x: 19, y: 30, desc: 'Push graded slabs straight to MySlabs with cert data, photos, and grade already filled in.' },
  { name: 'Square', logo: '/logos/square.svg', x: 83, y: 64, desc: "Swipe at a show, it's gone from your other channels automatically. No more selling the same card twice." },
  { name: 'Whatnot', logo: '/logos/whatnot.png', x: 17, y: 64, desc: 'Sell on Whatnot through your Shopify store: live shows and Buy It Now, synced to the same inventory.' },
  { name: 'TikTok Shop', logo: '/logos/tiktok.svg', x: 50, y: 90, desc: 'List your cards to TikTok Shop and sell them where the buyers are scrolling.' },
];

const FEATURES = [
  { icon: 'inventory', title: 'Smart Inventory', desc: 'Track every card: photos, grades, values, cost basis, location. Bulk import your catalog in minutes.' },
  { icon: 'accounting', title: 'Full Accounting Suite', desc: 'Real P&L, balance sheet, tax center, expense tracking. Real marketplace fees, true profit per channel.' },
  { icon: 'grade', title: 'PSA Submission Import', desc: 'Drop in the PDF, every card is logged. Drop in the receipt CSV, every grade and cert number applies. Stop typing 50 cards by hand.' },
  { icon: 'shipping', title: 'Shipping & Labels', desc: 'Compare carrier rates, buy labels in bulk, auto-track deliveries. One click from order to shipped.' },
  { icon: 'crm', title: 'Customer CRM', desc: 'Auto-built buyer profiles from every order. Tags, notes, purchase history, lifetime value.' },
  { icon: 'consignment', title: 'Consignment Tracking', desc: 'Manage consignor items, commission rates, and payments owed. Professional consignment built right in.' },
  { icon: 'cert', title: 'PSA / BGS / SGC Cert Lookup', desc: 'Enter a cert number, instantly pull grade and card details. Verify before you buy.' },
  { icon: 'reprice', title: 'Smart Repricing', desc: 'Price against the cheapest comps, grade for grade. Undercut to win the sale or match the market, on your schedule, with floors, ceilings and max-move caps keeping you in control.' },
  { icon: 'purchase', title: 'eBay Purchase Import', desc: 'Buy cards on eBay? Import your purchases straight into inventory with cost auto-filled. Track your whole pipeline.' },
];

// m = monthly price, a = annual TOTAL (2 months free). null annual = no yearly option.
const PRICING = [
  { name: 'Starter', m: 19.99, a: 199, cards: '200 cards · 100 AI credits/mo', features: ['200 inventory items', '100 AI credits/month', 'AI listing descriptions', 'Customer database', 'Shipping labels', 'Expense tracking'], cta: 'Get Started', highlight: false },
  { name: 'Dealer', m: 59.99, a: 599, cards: '5,000 cards · 1,000 AI credits/mo', features: ['5,000 inventory items', '1,000 AI credits/month', 'Full accounting suite (P&L, balance, tax)', 'Unlimited consignors', 'Multi-platform & bulk listing', 'Priority support'], cta: 'Get Started', highlight: true },
  { name: 'Business', m: 299.99, a: 2999, cards: 'Unlimited cards · 5,000 AI credits/mo', features: ['Unlimited inventory', '5,000 AI credits/month', 'Everything in Dealer', 'Multi-user accounts', 'Connect multiple eBay stores', 'API access (coming soon)', 'White-glove onboarding'], cta: 'Get Started', highlight: false },
];

// Detailed feature comparison, the moneyshot for shoppers who want to verify
// they're picking the right tier. True/false render as ✓/-; strings render as-is.
const COMPARISON_GROUPS = [
  {
    title: 'Core',
    rows: [
      { label: 'Inventory limit', values: ['200', '5,000', 'Unlimited'] },
      { label: 'AI credits per month (scans, descriptions, grade checks)', values: ['100', '1,000', '5,000'] },
      { label: 'AI card scanner & identification', values: [true, true, true] },
      { label: 'Cert lookups (PSA / BGS / SGC / CGC)', values: [true, true, true] },
      { label: 'Price to live market comps', values: [true, true, true] },
      { label: 'Order tracking', values: [true, true, true] },
      { label: 'Expense tracking', values: [true, true, true] },
    ],
  },
  {
    title: 'Sales Channels & Sync',
    rows: [
      { label: 'List & sync to eBay', values: [true, true, true] },
      { label: 'Public storefront', values: [true, true, true] },
      { label: 'Shopify', values: [false, true, true] },
      { label: 'Whatnot (through your Shopify store)', values: [false, true, true] },
      { label: 'Square (POS)', values: [false, true, true] },
      { label: 'TikTok Shop', values: [false, true, true] },
      { label: 'MySlabs', values: [false, true, true] },
      { label: 'Connect multiple eBay stores', values: [false, false, true] },
    ],
  },
  {
    title: 'Growing Dealer',
    rows: [
      { label: 'AI listing descriptions', values: [true, true, true] },
      { label: 'Customer database (CRM)', values: [true, true, true] },
      { label: 'Shipping labels at discount', values: [true, true, true] },
    ],
  },
  {
    title: 'Automation & Pricing',
    rows: [
      { label: 'Auto-pricing / Smart Repricing', values: [false, true, true] },
      { label: 'Auto-markdown rules', values: [false, true, true] },
      { label: 'Per-channel price markups', values: [true, true, true] },
      { label: 'CardHedger live market comps', values: [false, true, true] },
    ],
  },
  {
    title: 'Full Accounting & Grading',
    rows: [
      { label: 'P&L, Balance Sheet, Tax Center', values: [false, true, true] },
      { label: 'Inventory valuation', values: [false, true, true] },
      { label: 'Consignment tracking', values: [false, 'Unlimited', 'Unlimited'] },
      { label: 'Grading workflow (PSA submission + receipt import)', values: [false, true, true] },
      { label: '"Should I Grade This?" AI', values: [false, true, true] },
    ],
  },
  {
    title: 'Team & Scale',
    rows: [
      { label: 'Bulk listing tools', values: [false, true, true] },
      { label: 'Priority support', values: [false, true, true] },
      { label: 'Team members (own logins + permissions)', values: ['1', '2', '4'] },
      { label: 'API access', values: [false, false, 'Coming soon'] },
      { label: 'White-glove onboarding', values: [false, false, true] },
    ],
  },
];

const TESTIMONIALS = [
  { quote: "The accounting page showed me I was paying $9k a year in marketplace fees and had no idea. Fixed my pricing that same week. Wish I'd had this two years ago.", name: 'Mike D.', role: 'Full-time dealer, Ohio', initials: 'MD', color: '#1c56cc' },
  { quote: "I used to spend Sunday nights writing descriptions. Now I scan a box on Saturday morning, hit bulk generate, and I'm done. Genuinely don't know how I did it before.", name: 'Chris L.', role: 'eBay reseller, 1,400+ listings', initials: 'CL', color: '#2f5488' },
  { quote: "Sold a card at a show while it was still live on eBay. Sold it twice. It was a nightmare. The Square sync fixed that permanently.", name: 'Tony R.', role: 'Weekend card show vendor', initials: 'TR', color: '#059669' },
];

const COMING_SOON = ['Facebook & Instagram', 'Mercari', 'MyCardPost', 'TCGPlayer'];

// Who it's for + the before/after that frames the problem the whole page solves.
const PERSONAS = [
  { who: 'The card show vendor', pain: 'You work a show all weekend, then burn Monday night typing every sale back into a spreadsheet.' },
  { who: 'The high-volume reseller', pain: "You've got 1,400 cards live on eBay and no real idea which ones make you money after fees." },
  { who: 'The shop owner', pain: 'You sell online and at the counter, and your stock never matches between the two.' },
];
const BEFORE = [
  'Inventory in a spreadsheet that is always a step behind',
  'The same card live on eBay and Shopify, it sells twice, you cancel an order',
  'No real idea what you made after fees and shipping',
  'A scanner in one app, a cross-lister in another, accounting in a third',
  'Grading, consignments, and show sales tracked on sticky notes',
];
const AFTER = [
  'One inventory that stays current on every channel',
  'A card sells once and comes off everywhere else automatically',
  'True profit after the real fees on every single sale',
  'Scan, price, list, and account for a card in one place',
  'Grading ROI, consignor payouts, and your show register, all built in',
];

// ─── Logo, CSS replica of the SCN brand mark ────────────────────────────────
function Logo({ height = 38 }) {
  const s = height / 42; // scale factor, base design at height=42
  return (
    <div style={{
      height, display: 'inline-flex', alignItems: 'center', flexShrink: 0,
      background: NAVY, border: `1px solid rgba(201,162,39,0.5)`,
      borderRadius: Math.round(7 * s), padding: `0 ${Math.round(14 * s)}px`,
      gap: Math.round(10 * s),
    }}>
      <span style={{
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: Math.round(22 * s), fontWeight: 900, color: GOLD,
        letterSpacing: '-0.5px', lineHeight: 1, flexShrink: 0,
      }}>SCN</span>
      <div style={{ width: 1, height: Math.round(22 * s), background: GOLD, opacity: 0.45, flexShrink: 0 }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: Math.round(2 * s), flexShrink: 0 }}>
        <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: Math.round(9.5 * s), fontWeight: 800, color: GOLD, letterSpacing: '0.08em', lineHeight: 1 }}>SPORTS CARD</span>
        <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: Math.round(7.5 * s), fontWeight: 600, color: `rgba(201,162,39,0.65)`, letterSpacing: '0.12em', lineHeight: 1 }}>NETWORK</span>
      </div>
    </div>
  );
}

// ─── FeatureIcon ─────────────────────────────────────────────────────────────
// Landing FAQ, tight objection-handlers, condensed from the full /faq page.
const FAQS = [
  { q: 'Can I try it before I pay?', a: 'Yes. Every plan starts with a 14-day free trial with full access to everything, and you can cancel any time before it ends. Import your cards, run the scanner, list to your channels, and see your real numbers first.' },
  { q: 'How accurate is the AI scanner?', a: 'Snap the front and back and the AI fills in the player, year, brand, set, card number, and parallel, then cross-references a catalog so the identification and title come out clean. You review and hit save.' },
  { q: 'Do you support graded slabs?', a: 'Yes. Look up any PSA, BGS, SGC, or CGC slab by its cert number and SCN pulls the card details, grade, and photo automatically. No retyping.' },
  { q: 'What channels can I sell on?', a: 'One master inventory syncs across eBay, Shopify, Square (for in-person shows), MySlabs, Walmart Marketplace, and your own SCN storefront. Sell a card anywhere and it updates everywhere, so you never sell the same card twice. More channels are on the way.' },
  { q: 'Are the fees and profit numbers real or estimated?', a: "Real. We pull your actual transaction fees straight from the marketplace, including eBay's Finances API, so promoted-listing ad fees and shipping costs that estimate-based tools miss are all counted. Your P&L is the true number, not a guess." },
  { q: 'Can I sell on my own website?', a: 'Yes. Every dealer gets a hosted storefront at your own handle plus a public collection share link, so buyers can browse and buy straight from you.' },
  { q: 'I sell a lot, will it handle my volume?', a: 'Yes. Dealer ($59.99/mo) covers 5,000 cards with unlimited consignors and bulk listing, and Business ($299.99/mo) is unlimited with multi-user, the ability to connect multiple eBay stores, and API access coming soon. Plenty of dealers run thousands of cards through it.' },
  { q: 'Can I switch from a spreadsheet or another tool?', a: 'Usually under an hour. Connect your store and we import your full active catalog, titles, photos, item specifics, and pricing, in about 30 seconds. CSV import and bulk cert numbers cover the rest, and your customer list auto-builds from order history.' },
  { q: 'Can I cancel anytime?', a: 'Anytime, no contract. Upgrade when you outgrow it and cancel whenever. Your data stays yours.' },
];

// The 5-step lifecycle spine, the backbone of the page. Each step links down to
// the section that proves it. Anchors: add (ways-to-add), integrations (orbit),
// pricing-auto (repricing), orders (fulfillment), money (accounting/P&L).
const LIFECYCLE = [
  { n: 1, title: 'Add it, any way', desc: 'Scan a photo, look up a slab by cert number, import a CSV, or connect eBay and pull your whole catalog in seconds.', anchor: 'add' },
  { n: 2, title: 'List everywhere', desc: 'One inventory lists to eBay, Square, Shopify, MySlabs, and your own storefront. Sell it anywhere, it updates everywhere.', anchor: 'integrations' },
  { n: 3, title: 'Price on autopilot', desc: 'Live comps at listing, plus repricing on your schedule that pushes fresh prices to every channel on its own.', anchor: 'pricing-auto' },
  { n: 4, title: 'Sell & ship', desc: 'Manage orders and offers, then buy a discounted, tracked shipping label in one click.', anchor: 'orders' },
  { n: 5, title: 'Know your profit', desc: 'Real marketplace fees, true profit per card, and tax-ready books. No spreadsheet, no guessing.', anchor: 'money' },
];

// The many intake paths, the "so many ways to add" story. Icons reuse FeatureIcon.
const ADD_METHODS = [
  { icon: 'camera', title: 'AI photo scan', desc: 'Snap the front and back. The AI fills the player, year, set, number, and parallel, then you just hit save.' },
  { icon: 'cert', title: 'Slab / cert lookup', desc: 'Enter a PSA, BGS, SGC, or CGC cert number and SCN pulls the card, grade, and photo automatically.' },
  { icon: 'inventory', title: 'Bulk CSV import', desc: 'Bring a spreadsheet? Map your columns once and import thousands of cards in a single pass.' },
  { icon: 'accounting', title: 'Cost-sheet import', desc: 'Import a cost spreadsheet to set cost basis across your inventory in bulk, so profit is right from day one.' },
  { icon: 'edit', title: 'Manual entry', desc: 'Add a card by hand in seconds when you want full control over every detail.' },
  { icon: 'purchase', title: 'Connect a channel', desc: 'Connect a sales channel and pull your entire active catalog, titles, photos, and pricing, in about 30 seconds.' },
];

function FeatureIcon({ name, size = 22 }) {
  const icons = {
    inventory: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M20 7H4a1 1 0 00-1 1v11a1 1 0 001 1h16a1 1 0 001-1V8a1 1 0 00-1-1zM16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2M9 12h6M9 16h4" />,
    accounting: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />,
    shipping: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />,
    crm: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />,
    consignment: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />,
    cert: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />,
    camera: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z M15 13a3 3 0 11-6 0 3 3 0 016 0z" />,
    edit: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />,
    price: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />,
    grade: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />,
    reprice: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />,
    purchase: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007z" />,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      {icons[name]}
    </svg>
  );
}

// ─── Hooks & helpers ─────────────────────────────────────────────────────────
function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function FadeIn({ children, delay = 0, style = {} }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(20px)', transition: `opacity 0.55s ease ${delay}s, transform 0.55s ease ${delay}s`, ...style }}>
      {children}
    </div>
  );
}

// Interactive AI scan demo
function InteractiveDemo() {
  const SCAN_API = (process.env.REACT_APP_API_URL || 'http://localhost:3001') + '/api/public-scan';
  const [phase, setPhase] = useState('idle'); // idle | ready | scanning | done | error
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [errMsg, setErrMsg] = useState('');
  const fileRef = useRef(null);
  const b64Ref = useRef(null);

  const compress = (srcDataUrl) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const max = 1568;
      let w = img.width, h = img.height;
      if (Math.max(w, h) > max) { const s = max / Math.max(w, h); w = Math.round(w * s); h = Math.round(h * s); }
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      c.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(c.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => resolve(null);
    img.src = srcDataUrl;
  });

  const runScan = async (b64) => {
    setPhase('scanning'); setErrMsg('');
    try {
      const res = await fetch(SCAN_API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: b64, mediaType: 'image/jpeg' }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) { setErrMsg(data.message || 'Scan failed. Try a clearer, straight-on photo.'); setPhase('error'); return; }
      setResult(data.card); setPhase('done');
    } catch (e) { setErrMsg('Network hiccup. Give it another try.'); setPhase('error'); }
  };

  const onFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const compressed = await compress(ev.target.result);
      if (!compressed) { setErrMsg("Couldn't read that image. Try another."); setPhase('error'); return; }
      setPreview(compressed); b64Ref.current = compressed.split(',')[1];
      setResult(null); setErrMsg(''); setPhase('ready');
    };
    reader.readAsDataURL(f);
  };

  const trySample = async () => {
    try {
      const r = await fetch('/screenshots/Rickey.jpg.png');
      const blob = await r.blob();
      const dataUrl = await new Promise((res) => { const rd = new FileReader(); rd.onload = () => res(rd.result); rd.readAsDataURL(blob); });
      const compressed = await compress(dataUrl);
      setPreview(compressed); setResult(null);
      runScan(compressed.split(',')[1]);
    } catch (e) { setErrMsg("Couldn't load the sample. Upload your own card instead."); setPhase('error'); }
  };

  const reset = () => {
    setPhase('idle'); setPreview(null); setResult(null); setErrMsg('');
    b64Ref.current = null; if (fileRef.current) fileRef.current.value = '';
  };

  const money = (n) => (n == null ? null : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

  const rows = result ? [
    ['Player', result.playerName], ['Year', result.year], ['Brand', result.brand],
    ['Set', result.setName], ['Card #', result.cardNumber],
    ['Parallel', result.parallelVariety || 'Base'], ['Team', result.team], ['Sport', result.sport],
  ].filter((r) => r[1]) : [];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 32, alignItems: 'start' }} className="lp-grid-2">
      {/* Left, upload / preview + scan */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ background: '#0a1228', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14, padding: 24 }}>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} style={{ display: 'none' }} />
          {!preview ? (
            <button onClick={() => fileRef.current && fileRef.current.click()} style={{
              width: '100%', aspectRatio: '2.5/3.5', borderRadius: 10, cursor: 'pointer',
              background: 'linear-gradient(145deg, #1a2744, #0d1b3e)', border: '1.5px dashed rgba(201,162,39,0.5)',
              color: 'rgba(255,255,255,0.7)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 20,
            }}>
              <div style={{ fontSize: 40 }}>📷</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Drop a card photo</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>or tap to upload / take a photo</div>
            </button>
          ) : (
            <div style={{
              width: '100%', aspectRatio: '2.5/3.5', borderRadius: 10, overflow: 'hidden', position: 'relative',
              background: '#0d1b3e', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {phase === 'scanning' && (
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, transparent, #c9a227, transparent)', animation: 'scanLine 1.2s ease-in-out infinite', zIndex: 2 }} />
              )}
              <img src={preview} alt="Your card" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              {phase === 'done' && (
                <div style={{ position: 'absolute', top: 12, right: 12, background: '#16a34a', color: 'white', fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 100 }}>IDENTIFIED</div>
              )}
            </div>
          )}

          {preview && phase === 'ready' && (
            <button onClick={() => runScan(b64Ref.current)} style={{
              marginTop: 20, width: '100%', padding: '14px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg, #c9a227, #a88420)', color: '#0d1b3e', fontSize: 15, fontWeight: 700, cursor: 'pointer',
            }}>Scan This Card</button>
          )}
          {phase === 'scanning' && (
            <div style={{ marginTop: 18, fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>AI reading your card…</div>
          )}
          {(phase === 'done' || phase === 'error') && (
            <button onClick={reset} style={{
              marginTop: 20, width: '100%', padding: '12px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.15)',
              background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.75)', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}>Scan another card</button>
          )}
          {phase === 'idle' && (
            <div style={{ marginTop: 16, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
              No card handy? <button onClick={trySample} style={{ background: 'none', border: 'none', color: '#c9a227', fontWeight: 700, cursor: 'pointer', fontSize: 12, padding: 0 }}>Try a sample →</button>
            </div>
          )}
        </div>
        <style>{`@keyframes scanLine { 0%{top:0} 50%{top:100%} 100%{top:0} }`}</style>
      </div>

      {/* Right, real results */}
      <div>
        <div style={{ background: '#0a1228', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14, padding: 24, minHeight: 380 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: phase === 'done' ? '#16a34a' : phase === 'scanning' ? '#c9a227' : phase === 'error' ? '#d64545' : '#444' }} />
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              {phase === 'idle' ? 'Upload a card to see it identified' : phase === 'ready' ? 'Ready, hit Scan' : phase === 'scanning' ? 'Analyzing image…' : phase === 'error' ? 'Try again' : 'Card identified'}
            </span>
          </div>

          {phase === 'error' && (
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5, padding: '20px 0' }}>{errMsg}</div>
          )}

          {(phase === 'idle' || phase === 'ready') && (
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', lineHeight: 1.6, padding: '10px 0' }}>
              Snap a real sports card and the AI fills in the player, year, set, card number, and parallel, then cross-references a catalog and pulls a market price. The exact scan that lives inside the app.
            </div>
          )}

          {phase === 'scanning' && (
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', padding: '10px 0' }}>Reading the card…</div>
          )}

          {phase === 'done' && result && (
            <>
              {rows.map((row) => (
                <div key={row[0]} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: 8, marginBottom: 6,
                  background: 'rgba(201,162,39,0.08)', border: '1px solid rgba(201,162,39,0.2)',
                }}>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>{row[0]}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#c9a227' }}>{row[1]}</span>
                </div>
              ))}
              {result.marketPrice != null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderRadius: 8, marginBottom: 6, background: 'rgba(22,163,74,0.12)', border: '1px solid rgba(22,163,74,0.3)' }}>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', fontWeight: 700 }}>Market Price</span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: '#22c55e' }}>{money(result.marketPrice)}</span>
                </div>
              )}
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Link to="/register" style={{ display: 'inline-block', padding: '12px 32px', borderRadius: 8, background: 'linear-gradient(135deg, #c9a227, #a88420)', color: '#0d1b3e', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>Start your free trial to save this card →</Link>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 8 }}>14-day free trial · scan, price &amp; list your whole collection</div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Browser-chrome screenshot frame
function AppScreen({ src, alt, crop = 100, zoom = 1 }) {
  const zoomPct = `${zoom * 100}%`;
  const offsetPct = `${((zoom - 1) / 2) * -100}%`;
  return (
    <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.1)', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
      <div style={{ height: 32, background: '#f0f0f5', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', padding: '0 12px', gap: 6 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f57' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#febc2e' }} />
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#28c840' }} />
        <div style={{ marginLeft: 12, background: 'white', borderRadius: 5, padding: '3px 14px', fontSize: 10, color: '#999' }}>sportscardnetwork.ai</div>
      </div>
      <div style={{ overflow: 'hidden' }}>
        <img src={src} alt={alt} style={{ width: zoomPct, marginLeft: offsetPct, display: 'block', marginBottom: `${-(100 - crop)}%` }} />
      </div>
    </div>
  );
}

function CheckItem({ children, color = '#1a36d5', bg = '#eef0ff', border = '#c7d0ff' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ width: 20, height: 20, borderRadius: '50%', background: bg, border: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color, flexShrink: 0 }}>✓</div>
      <span style={{ fontSize: 14, color: '#444', fontWeight: 500 }}>{children}</span>
    </div>
  );
}

// Full-bleed split section, consistent background both sides, screenshot fills its half
// Inline mockup of the PSA submission import preview, used by the Grading
// SplitSection in place of a real screenshot. Looks like the live preview
// modal: header with submission # and service level, a 3-card table with
// editable raw-cost inputs and a MATCHED chip on the auto-matched row.
// Static, no interaction handlers, purely for hero/feature illustration.
function PsaPreviewMockup() {
  const NAVY = '#0d1b3e';
  const PURPLE = '#2f5488';
  const rowDivider = '1px solid #f0f1f5';
  const colTemplate = '1fr 88px 76px';
  const cards = [
    { name: '2023 Topps Chrome Bobby Witt Jr #150', rawCost: '45.00', declared: '250', matched: true },
    { name: '1989 Upper Deck Ken Griffey Jr RC #1', rawCost: '', declared: '500', matched: false },
    { name: '2018 Donruss Luka Doncic RC #248', rawCost: '12.00', declared: '80', matched: true },
  ];
  return (
    <div style={{ width: '100%', maxWidth: 520, margin: '0 auto', borderRadius: 14, background: '#fff', boxShadow: '0 30px 60px -20px rgba(13,27,62,0.35), 0 12px 30px -10px rgba(13,27,62,0.25)', border: '1px solid #e8e9ee', overflow: 'hidden', fontFamily: "'Manrope', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Subtle pulse on the dropzone so the page feels alive, slow, low-amplitude
          so it reads as "active surface" not "alarm". */}
      <style>{`
        @keyframes scn-psa-dropzone-pulse {
          0%, 100% { background: rgba(124,58,237,0.05); border-color: rgba(124,58,237,0.25); }
          50%      { background: rgba(124,58,237,0.13); border-color: rgba(124,58,237,0.55); }
        }
        .scn-psa-dropzone {
          animation: scn-psa-dropzone-pulse 2.6s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .scn-psa-dropzone { animation: none; }
        }
      `}</style>
      {/* Drop zone affordance */}
      <div className="scn-psa-dropzone" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'rgba(124,58,237,0.06)', borderBottom: '1px dashed rgba(124,58,237,0.35)' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PURPLE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <div style={{ fontSize: 12.5, color: PURPLE, fontWeight: 700 }}>PSA submission PDF dropped, 1 submission parsed</div>
      </div>
      {/* Submission header */}
      <div style={{ padding: '14px 16px', borderBottom: rowDivider }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: NAVY }}>Submission #14737000</div>
        <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Value Plus &middot; 3 cards &middot; est. fees $87.00</div>
      </div>
      {/* Table header */}
      <div style={{ display: 'grid', gridTemplateColumns: colTemplate, gap: 8, padding: '8px 16px', fontSize: 10, fontWeight: 800, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', background: '#fafbfc', borderBottom: rowDivider }}>
        <div>Card</div>
        <div style={{ textAlign: 'right' }}>Raw cost</div>
        <div style={{ textAlign: 'right' }}>Declared</div>
      </div>
      {/* Card rows */}
      {cards.map((c, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: colTemplate, gap: 8, alignItems: 'center', padding: '11px 16px', borderBottom: i < cards.length - 1 ? rowDivider : 'none', fontSize: 13, color: '#141f38' }}>
          <div style={{ minWidth: 0, overflow: 'hidden' }}>
            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</div>
            {c.matched && (
              <span style={{ display: 'inline-block', marginTop: 4, fontSize: 9, fontWeight: 800, background: '#dbeafe', color: '#1c56cc', padding: '2px 7px', borderRadius: 4, letterSpacing: '0.04em' }}>MATCHED</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, background: c.rawCost ? '#fff' : '#fafbfc', color: c.rawCost ? NAVY : '#bbb', fontSize: 13, fontWeight: 600 }}>
            {c.rawCost ? `$${c.rawCost}` : '-'}
          </div>
          <div style={{ textAlign: 'right', color: '#777', fontSize: 12.5 }}>${c.declared}</div>
        </div>
      ))}
      {/* Footer button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 16px', background: '#fafbfc', borderTop: rowDivider }}>
        <div style={{ padding: '8px 14px', fontSize: 12, fontWeight: 700, color: '#666', border: '1px solid #e5e7eb', borderRadius: 7, background: '#fff' }}>Cancel</div>
        <div style={{ padding: '8px 16px', fontSize: 12, fontWeight: 800, color: '#fff', background: NAVY, borderRadius: 7 }}>Create Submission</div>
      </div>
    </div>
  );
}

function SplitSection({ id, screenshotSrc, screenshotAlt, screenshotCrop = 100, screenshotZoom = 1, screenshotSide = 'left', customVisual, bg = '#f5f6fa', label, labelColor = '#1a36d5', labelBg = 'rgba(26,54,213,0.08)', title, body, checks, checkColor = '#1a36d5', checkBg = 'rgba(26,54,213,0.07)', checkBorder = 'rgba(26,54,213,0.15)' }) {
  const isLeft = screenshotSide === 'left';
  const screenshotPanel = (
    <div style={{ flex: '0 0 54%', background: bg, overflow: 'hidden', display: 'flex', alignItems: 'center', padding: isLeft ? '52px 24px 52px 64px' : '52px 64px 52px 24px' }}>
      {customVisual || <AppScreen src={screenshotSrc} alt={screenshotAlt} crop={screenshotCrop} zoom={screenshotZoom} />}
    </div>
  );
  const textPanel = (
    <FadeIn style={{ flex: 1, background: bg, display: 'flex', alignItems: 'center', padding: '64px 72px' }}>
      <div>
        <div style={{ display: 'inline-block', background: labelBg, color: labelColor, fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', padding: '5px 14px', borderRadius: 100, marginBottom: 18 }}>{label}</div>
        <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 38, fontWeight: 800, color: '#0d1b3e', letterSpacing: '-1.2px', lineHeight: 1.12, marginBottom: 16 }}>{title}</h2>
        <p style={{ fontSize: 15, color: '#555', lineHeight: 1.75, marginBottom: 28 }}>{body}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
          {checks.map(c => <CheckItem key={c} color={checkColor} bg={checkBg} border={checkBorder}>{c}</CheckItem>)}
        </div>
      </div>
    </FadeIn>
  );
  return (
    <section id={id} style={{ display: 'flex', background: bg, overflow: 'hidden' }} className={`lp-split${isLeft ? ' lp-split-left' : ''}`}>
      {isLeft ? screenshotPanel : textPanel}
      {isLeft ? textPanel : screenshotPanel}
    </section>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001') + '/api';

export default function LandingPage() {
  const [scrollY, setScrollY] = useState(0);
  const [stats, setStats] = useState(STATS_FALLBACK);
  const [showVideo, setShowVideo] = useState(false); // hero "Watch overview" modal
  const [annual, setAnnual] = useState(false); // pricing billing toggle
  const [nlEmail, setNlEmail] = useState('');
  const [nlMsg, setNlMsg] = useState('');
  const [nlLoading, setNlLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!nlEmail.trim()) return;
    setNlLoading(true);
    try {
      const res = await fetch(`${API}/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: nlEmail, source: 'landing' }),
      });
      const data = await res.json();
      setNlMsg(data.message || "You're in!");
      setNlEmail('');
    } catch {
      setNlMsg('Something went wrong. Try again.');
    }
    setNlLoading(false);
  };

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Live platform stats for the hero (cached server-side). Falls back silently
  // to STATS_FALLBACK on any error so the hero never shows blank/loading.
  useEffect(() => {
    const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001') + '/api';
    fetch(`${API}/public/stats`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!d || typeof d !== 'object') return;
        setStats(STATS_FALLBACK.map(s => ({
          ...s,
          value: d[s.key] != null ? (s.fmt === 'money' ? fmtMoney(d[s.key]) : fmtCount(d[s.key])) : s.value,
          num: d[s.key] != null ? Number(d[s.key]) : s.num,
        })));
      })
      .catch(() => {});
  }, []);

  return (
    <div style={{ fontFamily: "'Manrope', sans-serif", color: '#0a0a1a', background: '#f5f6fa', minHeight: '100vh', overflowX: 'hidden' }}>
      {/* Fonts are preloaded in public/index.html, injecting a stylesheet link
          here loaded it post-mount and caused a font-swap reflow (CLS). */}
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        .lp-btn { background: ${GOLD}; color: ${NAVY}; padding: 13px 30px; border-radius: 8px; font-size: 15px; font-weight: 800; text-decoration: none; display: inline-block; transition: all 0.2s; border: none; cursor: pointer; font-family: 'Manrope', sans-serif; letter-spacing: 0.01em; }
        .lp-btn:hover { background: #d4ac2e; transform: translateY(-1px); box-shadow: 0 6px 20px rgba(201,162,39,0.4); }
        .lp-btn-ghost { background: transparent; color: white; padding: 13px 30px; border-radius: 8px; font-size: 15px; font-weight: 700; text-decoration: none; display: inline-block; transition: all 0.2s; border: 1px solid rgba(255,255,255,0.25); cursor: pointer; font-family: 'Manrope', sans-serif; }
        .lp-btn-ghost:hover { background: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.5); }
        .lp-btn-ghost-light { background: #fff; color: ${NAVY}; padding: 13px 30px; border-radius: 8px; font-size: 15px; font-weight: 700; text-decoration: none; display: inline-block; transition: all 0.2s; border: 1px solid #e2e5ee; cursor: pointer; font-family: 'Manrope', sans-serif; }
        .lp-btn-ghost-light:hover { border-color: ${NAVY}; transform: translateY(-1px); box-shadow: 0 6px 18px rgba(13,27,62,0.1); }
        .lp-nav-link { color: rgba(255,255,255,0.75); font-size: 14px; font-weight: 500; text-decoration: none; transition: color 0.2s; }
        .lp-nav-link:hover { color: ${GOLD}; }
        .lp-nav-link-scrolled { color: #444; font-size: 14px; font-weight: 500; text-decoration: none; transition: color 0.2s; }
        .lp-nav-link-scrolled:hover { color: ${NAVY}; }
        .lp-feature-card { background: #fafbff; border-radius: 14px; padding: 24px; border: 1px solid #eef0ff; transition: all 0.25s; }
        .lp-feature-card:hover { transform: translateY(-3px); box-shadow: 0 12px 36px rgba(13,27,62,0.08); border-color: #c7d0ff; }
        .lp-platform-card { background: white; border-radius: 14px; padding: 24px; border: 1px solid #eee; transition: all 0.25s; }
        .lp-platform-card:hover { transform: translateY(-3px); box-shadow: 0 10px 30px rgba(0,0,0,0.07); }
        .lp-pricing-card { background: white; border-radius: 18px; padding: 30px; border: 1px solid #eee; display: flex; flex-direction: column; height: 100%; transition: all 0.25s; }
        .lp-pricing-card:hover { transform: translateY(-3px); box-shadow: 0 12px 36px rgba(0,0,0,0.07); }
        .lp-pricing-card.highlight { background: ${NAVY}; border-color: ${NAVY}; }
        .lp-testimonial { background: #fafbff; border-radius: 14px; padding: 28px; border: 1px solid #eee; transition: all 0.25s; }
        .lp-testimonial:hover { box-shadow: 0 10px 30px rgba(0,0,0,0.06); transform: translateY(-2px); }
        .lp-spine { display: grid; grid-template-columns: repeat(5, 1fr); gap: 0; position: relative; }
        .lp-spine::before { content: ''; position: absolute; top: 25px; left: 10%; right: 10%; height: 2px; background: #e3e6ef; z-index: 0; }
        .lp-spine-step { position: relative; z-index: 1; text-align: center; padding: 0 14px; text-decoration: none; color: inherit; }
        .lp-spine-num { width: 50px; height: 50px; border-radius: 50%; background: ${NAVY}; color: ${GOLD}; display: flex; align-items: center; justify-content: center; font-size: 19px; font-weight: 800; margin: 0 auto 16px; box-shadow: 0 6px 18px rgba(13,27,62,0.18); transition: transform 0.2s, box-shadow 0.2s; }
        .lp-spine-step:hover .lp-spine-num { transform: translateY(-3px); box-shadow: 0 10px 24px rgba(13,27,62,0.25); }
        .lp-spine-title { font-weight: 800; font-size: 16px; color: ${NAVY}; margin-bottom: 7px; letter-spacing: -0.2px; }
        .lp-spine-desc { font-size: 13.5px; color: #66708a; line-height: 1.55; margin: 0; }
        @media (max-width: 960px) {
          .lp-nav-links { display: none !important; }
          .lp-mobile-nav-btns { display: flex !important; }
          .lp-hero-grid { grid-template-columns: 1fr !important; }
          .lp-hero-mockup { display: none !important; }
          .lp-split { flex-direction: column !important; }
          .lp-split-left { flex-direction: column-reverse !important; } /* keep description ABOVE the screenshot on mobile */
          .lp-split > div { flex: unset !important; width: 100% !important; padding: 32px 20px !important; border-radius: 0 !important; }
          .lp-grid-2 { grid-template-columns: 1fr !important; }
          .lp-grid-3 { grid-template-columns: 1fr !important; }
          .lp-grid-4 { grid-template-columns: 1fr 1fr !important; }
          .lp-grid-5 { grid-template-columns: 1fr !important; }
          .lp-spine { grid-template-columns: 1fr !important; gap: 20px !important; }
          .lp-spine::before { display: none !important; }
          .lp-spine-step { display: flex !important; gap: 14px; align-items: flex-start; text-align: left !important; padding: 0 !important; }
          .lp-spine-num { margin: 0 !important; flex-shrink: 0; width: 44px !important; height: 44px !important; font-size: 17px !important; }
          .lp-compare-hint { display: block !important; }
          .lp-hero-title { font-size: 36px !important; letter-spacing: -1px !important; }
          .lp-stats-row { flex-wrap: wrap; gap: 0 !important; }
          .lp-stat-sep { display: none !important; }
          .lp-waitlist-form { flex-direction: column !important; }
          .lp-footer-cols { flex-direction: column !important; }
          .lp-section-pad { padding: 48px 20px !important; }
          .lp-hero-inner { padding: 48px 20px 0 !important; }
          .lp-nav { padding: 0 16px !important; }
          .lp-origin-quote { padding: 32px 20px !important; }
          .lp-waitlist-section { padding: 56px 20px !important; }
          .lp-footer { padding: 40px 20px 24px !important; }
          .lp-hero-btns { flex-direction: column !important; align-items: stretch !important; }
          .lp-hero-btns a { text-align: center !important; }
          .lp-hero-text { padding-bottom: 32px !important; }
          .lp-mobile-nav-btns a { min-height: 44px; display: inline-flex !important; align-items: center; justify-content: center; }
          .lp-footer a { display: inline-block; padding: 8px 0; }
          .lp-footer-cols > div:last-child { gap: 24px 40px !important; }
          button[aria-label="Close video"] { width: 44px !important; height: 44px !important; }
        }
        @media (max-width: 600px) {
          section h2 { font-size: 30px !important; letter-spacing: -0.8px !important; line-height: 1.15 !important; }
          .lp-btn, .lp-btn-ghost, .lp-btn-ghost-light { max-width: 100%; }
        }
      `}</style>

      {/* ── NAV ─────────────────────────────────────────────────────────── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: 'rgba(250,251,255,0.88)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
        borderBottom: `1px solid ${scrollY > 20 ? '#e8eaf0' : 'transparent'}`,
        transition: 'border 0.3s',
        padding: '0 48px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }} className="lp-nav">
        <Logo height={36} />
        <div className="lp-nav-links" style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
          {NAV_LINKS.map(l => (
            <a key={l} className="lp-nav-link-scrolled" href={`#${l.toLowerCase()}`} onClick={e => { e.preventDefault(); const el = document.getElementById(l.toLowerCase()); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}>{l}</a>
          ))}
          <Link className="lp-nav-link-scrolled" to="/login">Sign In</Link>
          <Link className="lp-btn" to="/register" style={{ padding: '9px 20px', fontSize: 13 }}>Start Free Trial</Link>
        </div>
        {/* Mobile nav buttons, hidden on desktop */}
        <div className="lp-mobile-nav-btns" style={{ display: 'none', gap: 8, alignItems: 'center' }}>
          <Link to="/login" style={{ fontSize: 13, fontWeight: 600, color: '#444', textDecoration: 'none', padding: '8px 12px' }}>Sign In</Link>
          <Link className="lp-btn" to="/register" style={{ padding: '8px 16px', fontSize: 13 }}>Start Free Trial</Link>
        </div>
      </nav>

      {/* ── HERO (Hybrid: light page, navy/gold accents, bold sans) ───────── */}
      <section style={{ background: 'var(--lp-bg, #fafbff)', paddingTop: 64 }}>
        <div style={{ maxWidth: 1440, margin: '0 auto', padding: '24px 56px 0', display: 'grid', gridTemplateColumns: '1fr 1.45fr', gap: 44, alignItems: 'center' }} className="lp-hero-grid lp-hero-inner">
          {/* Left */}
          <FadeIn>
            <div className="lp-hero-text" style={{ paddingBottom: 72 }}>
              <h1 className="lp-hero-title" style={{ fontFamily: "'Manrope', sans-serif", fontSize: 54, fontWeight: 800, lineHeight: 1.07, letterSpacing: '-1.5px', color: NAVY, marginBottom: 22 }}>
                Run your card business{' '}
                <span style={{ color: GOLD }}>like a business.</span>
              </h1>
              <p style={{ fontSize: 18, lineHeight: 1.6, color: '#64748b', marginBottom: 32, maxWidth: 480 }}>
                Stop running your card business out of a spreadsheet. List everywhere, price to real comps, and see your true profit after fees, on a <strong style={{ color: NAVY }}>flat monthly rate. We never take a percentage of your sales.</strong>
              </p>
              <div className="lp-hero-btns" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
                <Link className="lp-btn" to="/register" style={{ fontSize: 17, padding: '16px 38px' }}>Start Free Trial →</Link>
                <button className="lp-btn-ghost-light" onClick={() => setShowVideo(true)} style={{ fontSize: 17, fontWeight: 800, padding: '16px 32px', display: 'inline-flex', alignItems: 'center', gap: 11, border: `2px solid ${GOLD}` }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', background: GOLD }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill={NAVY}><path d="M8 5v14l11-7z"/></svg>
                  </span>
                  Watch 1-minute overview
                </button>
                <a className="lp-btn-ghost-light" href="https://calendly.com/zfinkel1/scn-dealer-demo" target="_blank" rel="noopener noreferrer" style={{ fontSize: 15, padding: '15px 30px' }}>Book a demo</a>
              </div>
              <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 32 }}>
                Free trial · setup in 30 seconds · High-volume dealer?{' '}
                <a href="tel:7732412622" style={{ color: '#64748b', textDecoration: 'none', fontWeight: 700 }}>Call 773-241-2622</a>
              </p>
              {/* Inline stats */}
              <div className="lp-stats-row" style={{ display: 'flex', gap: 0, borderTop: '1px solid #e8eaf0', paddingTop: 26 }}>
                {stats.map((s, i) => (
                  <React.Fragment key={s.label}>
                    <div style={{ paddingRight: 28 }}>
                      <div style={{ fontSize: 26, fontWeight: 800, color: NAVY, letterSpacing: '-0.5px' }}><CountUp num={s.num} fmt={s.fmt} /></div>
                      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{s.label}</div>
                    </div>
                    {i < stats.length - 1 && <div className="lp-stat-sep" style={{ width: 1, background: '#e8eaf0', margin: '0 28px 0 0', alignSelf: 'stretch' }} />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </FadeIn>
          {/* Right, dashboard screenshot in a light browser frame. Hidden on
              mobile: a wide DESKTOP screenshot crushes to a smear at phone width. */}
          <FadeIn delay={0.1}>
            <div className="lp-hero-mockup" style={{ display: 'flex', alignItems: 'center', height: '100%', paddingBottom: 56 }}>
              <div style={{ width: '100%', borderRadius: 14, overflow: 'hidden', boxShadow: '0 30px 70px -28px rgba(13,27,62,0.42), 0 12px 28px -12px rgba(13,27,62,0.18)', border: '1px solid #e8eaf0', background: '#fff' }}>
                <div style={{ height: 36, background: '#f1f3f9', display: 'flex', alignItems: 'center', padding: '0 14px', gap: 7, borderBottom: '1px solid #e8eaf0' }}>
                  <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#ff5f57' }} />
                  <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#febc2e' }} />
                  <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#28c840' }} />
                  <div style={{ marginLeft: 10, background: '#fff', border: '1px solid #e8eaf0', borderRadius: 6, padding: '3px 14px', fontSize: 11, color: '#94a3b8' }}>sportscardnetwork.ai/dashboard</div>
                </div>
                <div style={{ overflow: 'hidden' }}>
                  <img src={`${S}dashboard.png?v=2`} alt="Dashboard" width="1861" height="908" style={{ width: '100%', height: 'auto', aspectRatio: '1861 / 908', display: 'block' }} />
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── "WORKS WITH" trust strip, honest: our real integrations + graders ── */}
      <section style={{ background: '#fafbff', padding: '26px 64px', borderTop: '1px solid #eef0f5', borderBottom: '1px solid #e8e9ee' }} className="lp-section-pad">
        <div style={{ maxWidth: 1080, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'clamp(20px, 4vw, 48px)', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>Works with</span>
          {['eBay', 'Shopify', 'Square', 'MySlabs', 'Walmart', 'PSA', 'BGS', 'SGC', 'CGC'].map(n => (
            <span key={n} style={{ fontSize: 17, fontWeight: 800, color: '#aeb6c6', letterSpacing: '-0.3px' }}>{n}</span>
          ))}
        </div>
      </section>

      {/* ── SOUND FAMILIAR: who it's for + before/after (frames the problem) ── */}
      <section className="lp-section-pad" style={{ background: '#fafbff', padding: '64px 64px 60px', borderBottom: '1px solid #e8e9ee' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 36 }}>
              <div style={{ display: 'inline-block', background: 'rgba(13,27,62,0.07)', color: NAVY, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', padding: '5px 14px', borderRadius: 100, marginBottom: 14 }}>SOUND FAMILIAR?</div>
              <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 38, fontWeight: 800, color: NAVY, letterSpacing: '-1.3px', marginBottom: 12 }}>Running a card business shouldn't take ten open tabs.</h2>
              <p style={{ fontSize: 17, color: '#666', maxWidth: 620, margin: '0 auto', lineHeight: 1.6 }}>If any of these are you, you already know where the hours go.</p>
            </div>
          </FadeIn>
          <FadeIn delay={0.08}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(258px, 1fr))', gap: 16, marginBottom: 44 }}>
              {PERSONAS.map((p, i) => (
                <div key={i} style={{ background: 'white', border: '1px solid #e8e9ee', borderRadius: 14, padding: '20px 22px' }}>
                  <div style={{ fontSize: 15, color: '#3f4757', lineHeight: 1.55, fontStyle: 'italic' }}>"{p.pain}"</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#1C56CC', marginTop: 12, letterSpacing: '0.03em' }}>{p.who}</div>
                </div>
              ))}
            </div>
          </FadeIn>
          <FadeIn delay={0.12}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
              <div style={{ background: 'white', border: '1px solid #ece3e3', borderRadius: 16, padding: '26px 26px 20px' }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#a23b2d', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 14 }}>The old way</div>
                {BEFORE.map((t, i) => (
                  <div key={i} style={{ display: 'flex', gap: 11, alignItems: 'flex-start', padding: '9px 0', borderTop: i ? '1px solid #f1eeee' : 'none' }}>
                    <span style={{ color: '#c0493a', fontWeight: 800, flexShrink: 0, marginTop: 1 }}>✕</span>
                    <span style={{ fontSize: 14.5, color: '#5a6472', lineHeight: 1.5 }}>{t}</span>
                  </div>
                ))}
              </div>
              <div style={{ background: NAVY, borderRadius: 16, padding: '26px 26px 20px', boxShadow: '0 14px 44px rgba(13,27,62,0.18)' }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: GOLD, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 14 }}>With SportsCardNetwork</div>
                {AFTER.map((t, i) => (
                  <div key={i} style={{ display: 'flex', gap: 11, alignItems: 'flex-start', padding: '9px 0', borderTop: i ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
                    <span style={{ color: '#5ee08f', fontWeight: 800, flexShrink: 0, marginTop: 1 }}>✓</span>
                    <span style={{ fontSize: 14.5, color: 'rgba(255,255,255,0.9)', lineHeight: 1.5 }}>{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── HOW IT WORKS, 5-step lifecycle spine (the backbone) ──────────── */}
      <section id="how" className="lp-section-pad" style={{ background: 'white', padding: '64px 64px 60px', borderBottom: '1px solid #e8e9ee' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <div style={{ display: 'inline-block', background: 'rgba(13,27,62,0.07)', color: NAVY, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', padding: '5px 14px', borderRadius: 100, marginBottom: 14 }}>THE WHOLE WORKFLOW</div>
              <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 38, fontWeight: 800, color: NAVY, letterSpacing: '-1.3px', marginBottom: 12 }}>From card in hand to real profit. One system.</h2>
              <p style={{ fontSize: 17, color: '#666', maxWidth: 600, margin: '0 auto', lineHeight: 1.6 }}>Every step a card takes in your business lives in one place, so nothing slips through the cracks and you always know your true number.</p>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="lp-spine">
              {LIFECYCLE.map((s) => (
                <a key={s.n} className="lp-spine-step" href={`#${s.anchor}`} onClick={e => { e.preventDefault(); const el = document.getElementById(s.anchor); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}>
                  <div className="lp-spine-num">{s.n}</div>
                  <div>
                    <div className="lp-spine-title">{s.title}</div>
                    <p className="lp-spine-desc">{s.desc}</p>
                  </div>
                </a>
              ))}
            </div>
          </FadeIn>
          <FadeIn>
            <div style={{ textAlign: 'center', marginTop: 48 }}>
              <Link className="lp-btn" to="/register" style={{ fontSize: 16, padding: '16px 40px', background: NAVY, color: GOLD }}>Start Free Trial →</Link>
              <p style={{ fontSize: 12, color: '#aaa', marginTop: 10 }}>14-day free trial · Cancel anytime</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── ADD INVENTORY, multiple ways to get cards in ─────────────────── */}
      <section id="add" className="lp-section-pad" style={{ background: '#fafbff', padding: '72px 64px', borderBottom: '1px solid #e8e9ee' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ marginBottom: 40 }}>
              <div style={{ display: 'inline-block', background: 'rgba(13,27,62,0.07)', color: NAVY, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', padding: '5px 14px', borderRadius: 100, marginBottom: 14 }}>ADD INVENTORY</div>
              <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 42, fontWeight: 800, color: NAVY, letterSpacing: '-1.5px', marginBottom: 12 }}>Multiple ways to add your inventory</h2>
              <p style={{ fontSize: 17, color: '#666', maxWidth: 560, margin: 0, lineHeight: 1.6 }}>However you source your cards, get them in fast. Scan, import, or connect a channel, whatever fits how you already work.</p>
            </div>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }} className="lp-grid-3">
            {ADD_METHODS.map((m, i) => (
              <FadeIn key={m.title} delay={i * 0.06}>
                <div className="lp-feature-card" style={{ height: '100%' }}>
                  <div style={{ width: 42, height: 42, borderRadius: 10, background: 'rgba(13,27,62,0.06)', color: NAVY, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                    <FeatureIcon name={m.icon} size={22} />
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: NAVY, marginBottom: 6 }}>{m.title}</div>
                  <p style={{ fontSize: 13.5, color: '#666', lineHeight: 1.6, margin: 0 }}>{m.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.3}>
            <p style={{ fontSize: 14, color: '#666', marginTop: 22, textAlign: 'center' }}>
              Loading a big collection? <a href="/guides/bulk-scan-sports-cards" style={{ color: '#1C56CC', fontWeight: 700, textDecoration: 'none' }}>Bulk-scan a whole stack with a document scanner &rarr;</a>
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ── MULTI-CHANNEL ───────────────────────────────────────────────── */}
      <section id="integrations" className="lp-section-pad" style={{ background: '#f5f6fa', padding: '72px 64px', borderBottom: '1px solid #e8e9ee' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <div style={{ display: 'inline-block', background: '#eef0ff', color: '#1a36d5', fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', padding: '5px 14px', borderRadius: 100, marginBottom: 14 }}>MULTI-CHANNEL SELLING</div>
              <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 42, fontWeight: 800, color: '#0a0a1a', letterSpacing: '-1.5px', marginBottom: 12 }}>Every Channel. One Inventory.</h2>
              <p style={{ fontSize: 17, color: '#666', maxWidth: 500, margin: '0 auto' }}>Your cards live in one place. When something sells anywhere, everything updates. No manual work, no double-sells.</p>
            </div>
          </FadeIn>
          <FadeIn>
            <div className="lp-web" style={{ marginBottom: 40 }}>
              <div className="lp-web-grid" />
              <svg className="lp-web-lines" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                {PLATFORMS.map(p => (
                  <line key={p.name} x1="50" y1="50" x2={p.x} y2={p.y} stroke="#c3c9dc" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                ))}
              </svg>
              <div className="lp-web-node lp-web-center" style={{ left: '50%', top: '50%' }}>
                <div className="lp-web-tile"><img src="/favicon.svg" alt="SportsCardNetwork" /></div>
              </div>
              {PLATFORMS.map(p => (
                <div key={p.name} className="lp-web-node" style={{ left: `${p.x}%`, top: `${p.y}%` }} title={p.desc}>
                  <div className="lp-web-tile"><img src={p.logo} alt={p.name} loading="lazy" /></div>
                  <span className="lp-web-label">{p.name}</span>
                </div>
              ))}
            </div>
          </FadeIn>
          <FadeIn><div style={{ textAlign: 'center', fontSize: 14.5, color: '#888', fontWeight: 600 }}>More selling channels coming soon.</div></FadeIn>
        </div>
      </section>

      {/* ── POINT OF SALE, sell in person / at shows ────────────────────── */}
      <section id="pos" className="lp-section-pad" style={{ background: 'white', padding: '72px 64px', borderBottom: '1px solid #e8e9ee' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ marginBottom: 40 }}>
              <div style={{ display: 'inline-block', background: 'rgba(13,27,62,0.07)', color: NAVY, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', padding: '5px 14px', borderRadius: 100, marginBottom: 14 }}>POINT OF SALE, SELL IN PERSON</div>
              <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 42, fontWeight: 800, color: NAVY, letterSpacing: '-1.5px', marginBottom: 12 }}>Your card show register.</h2>
              <p style={{ fontSize: 17, color: '#666', maxWidth: 620, margin: 0, lineHeight: 1.6 }}>
                Not every card sells online. Ring up a sale right at your table, in a DM, or across the counter: tap the cards, take any payment, and hand over a receipt. The moment it sells it's <strong>pulled off eBay and every other channel</strong>, and it's already in your books. Your phone is the register, no extra hardware.
              </p>
            </div>
          </FadeIn>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }} className="lp-grid-3">
            {[
              { icon: 'price', title: 'Ring it up fast', desc: 'Tap the cards you\'re selling and take payment however they pay: cash, Venmo, Zelle, or a card. Built for a busy show table.' },
              { icon: 'crm', title: 'Receipts & invoices', desc: 'Print a receipt or email an invoice on the spot. Run a tab for a regular and mark it paid when they settle up.' },
              { icon: 'accounting', title: 'Off every channel, in your P&L', desc: 'Sold in person means gone from eBay and everywhere else instantly, so no double-sells, and the profit lands in your accounting automatically.' },
            ].map((f, i) => (
              <FadeIn key={f.title} delay={i * 0.07}>
                <div className="lp-feature-card" style={{ height: '100%' }}>
                  <div style={{ width: 42, height: 42, borderRadius: 10, background: 'rgba(13,27,62,0.06)', color: NAVY, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                    <FeatureIcon name={f.icon} size={22} />
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: NAVY, marginBottom: 6 }}>{f.title}</div>
                  <p style={{ fontSize: 13.5, color: '#666', lineHeight: 1.6, margin: 0 }}>{f.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn>
            <div style={{ textAlign: 'center', marginTop: 36, fontSize: 13.5, color: '#888' }}>
              Sell online, in person, or both. Every sale ends up in one inventory and one set of books.
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── ACCOUNTING, the moat ──────────────────────────────────────── */}
      <section id="money" className="lp-section-pad" style={{ background: '#f5f6fa', padding: '72px 64px', borderBottom: '1px solid #e8e9ee' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ marginBottom: 40 }}>
              <div style={{ display: 'inline-block', background: 'rgba(13,27,62,0.07)', color: NAVY, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', padding: '5px 14px', borderRadius: 100, marginBottom: 14 }}>REAL ACCOUNTING, NOT GUESSWORK</div>
              <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 42, fontWeight: 800, color: NAVY, letterSpacing: '-1.5px', marginBottom: 12 }}>Know exactly what every card made you.</h2>
              <p style={{ fontSize: 17, color: '#666', maxWidth: 620, margin: 0, lineHeight: 1.6 }}>
                Most dealers find out at tax time, or never. SCN tracks every sale across all your channels and pulls your <strong>real marketplace fees</strong> automatically, showing true profit per card, per month, per channel. One dealer found $9,000+ a year in fees he never knew he was paying.
              </p>
            </div>
          </FadeIn>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }} className="lp-grid-3">
            {[
              { icon: 'accounting', title: 'Real marketplace fees, automatic', desc: "Pulled straight from the source, including eBay's actual Finances API, not estimates. See exactly what each channel takes on every sale." },
              { icon: 'price', title: 'True profit per card', desc: "Add each card's cost basis. Every sale shows real profit: sale price minus real fees minus what you paid." },
              { icon: 'cert', title: 'Tax Center', desc: 'Tax-ready totals whenever you need them. No shoebox of receipts come April.' },
              { icon: 'consignment', title: 'Consignment tracking', desc: 'Track consignor cards, commission splits, and exactly what you owe each consignor.' },
              { icon: 'inventory', title: 'Live inventory value', desc: 'What your entire inventory is worth right now, at a glance.' },
              { icon: 'crm', title: 'Track every expense: supplies, grading fees & more', desc: 'Log every cost beyond your channels: supplies, shipping materials, show tables, grading fees, so your profit is the real number.' },
            ].map((f, i) => (
              <FadeIn key={f.title} delay={i * 0.07}>
                <div className="lp-feature-card" style={{ height: '100%' }}>
                  <div style={{ width: 42, height: 42, borderRadius: 10, background: 'rgba(13,27,62,0.06)', color: NAVY, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                    <FeatureIcon name={f.icon} size={22} />
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: NAVY, marginBottom: 6 }}>{f.title}</div>
                  <p style={{ fontSize: 13.5, color: '#666', lineHeight: 1.6, margin: 0 }}>{f.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn>
            <div style={{ textAlign: 'center', marginTop: 36, fontSize: 13.5, color: '#888' }}>
              Full P&amp;L and balance sheet included: real financial statements for your card business.
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── PRICING ON AUTOPILOT, spine step 3 ──────────────────────────── */}
      <section id="pricing-auto" className="lp-section-pad" style={{ background: 'white', padding: '72px 64px', borderBottom: '1px solid #e8e9ee' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ marginBottom: 40 }}>
              <div style={{ display: 'inline-block', background: 'rgba(13,27,62,0.07)', color: NAVY, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', padding: '5px 14px', borderRadius: 100, marginBottom: 14 }}>PRICING ON AUTOPILOT</div>
              <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 42, fontWeight: 800, color: NAVY, letterSpacing: '-1.5px', marginBottom: 12 }}>Priced right, without watching it.</h2>
              <p style={{ fontSize: 17, color: '#666', maxWidth: 580, margin: 0, lineHeight: 1.6 }}>Set your rules once. SCN prices new listings to live comps and re-checks your inventory every week, pushing fresh prices to every channel so nothing sits stale or sells cheap.</p>
            </div>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }} className="lp-grid-3">
            {[
              { icon: 'price', title: 'Live comps at listing', desc: 'Every new listing gets priced off what the card actually sold for recently, not a guess.' },
              { icon: 'reprice', title: 'Automatic weekly repricing', desc: 'SCN re-checks the market every week and adjusts your prices within the margin floors you set.' },
              { icon: 'inventory', title: 'Pushed to every channel', desc: 'New prices sync out to eBay, Shopify, Square, MySlabs, and your storefront on their own.' },
            ].map((m, i) => (
              <FadeIn key={m.title} delay={i * 0.06}>
                <div className="lp-feature-card" style={{ height: '100%' }}>
                  <div style={{ width: 42, height: 42, borderRadius: 10, background: 'rgba(13,27,62,0.06)', color: NAVY, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                    <FeatureIcon name={m.icon} size={22} />
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: NAVY, marginBottom: 6 }}>{m.title}</div>
                  <p style={{ fontSize: 13.5, color: '#666', lineHeight: 1.6, margin: 0 }}>{m.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── INTERACTIVE DEMO ──────────────────────────────────────────── */}
      <section className="lp-section-pad" style={{ background: NAVY, padding: '72px 64px', overflow: 'hidden' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <div style={{ display: 'inline-block', background: 'rgba(201,162,39,0.15)', color: GOLD, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', padding: '5px 14px', borderRadius: 100, marginBottom: 14, border: '1px solid rgba(201,162,39,0.3)' }}>TRY IT NOW</div>
              <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 38, fontWeight: 800, color: 'white', letterSpacing: '-1.2px', marginBottom: 8 }}>See the AI Scanner in Action</h2>
              <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.5)' }}>Click "Scan" and watch AI identify a card in seconds, no signup required.</p>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <InteractiveDemo />
          </FadeIn>
        </div>
      </section>

      {/* ── AI FEATURES ─────────────────────────────────────────────────── */}
      <section id="features" className="lp-section-pad" style={{ background: '#f5f6fa', padding: '72px 64px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ marginBottom: 48 }}>
              <div style={{ display: 'inline-block', background: `rgba(13,27,62,0.07)`, color: NAVY, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', padding: '5px 14px', borderRadius: 100, marginBottom: 14 }}>AI-POWERED</div>
              <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 42, fontWeight: 800, color: NAVY, letterSpacing: '-1.5px', marginBottom: 12 }}>The Stuff That Used to Take Hours</h2>
              <p style={{ fontSize: 17, color: '#666', maxWidth: 500, margin: 0 }}>Writing descriptions, identifying cards, figuring out what to grade. Handled. You focus on buying and selling.</p>
            </div>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 52 }} className="lp-grid-2">
            {AI_FEATURES.map((f, i) => (
              <FadeIn key={f.title} delay={i * 0.08}>
                <div style={{ background: 'white', border: '1px solid #e8e9ee', borderRadius: 14, padding: '26px', position: 'relative', overflow: 'hidden', boxShadow: '0 2px 8px rgba(13,27,62,0.05)' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${NAVY}, #1a36d5)` }} />
                  <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgba(13,27,62,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: NAVY, marginBottom: 14 }}>
                    <FeatureIcon name={f.icon} />
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: NAVY, marginBottom: 8 }}>{f.title}</div>
                  <p style={{ color: '#666', fontSize: 14, lineHeight: 1.65, margin: 0 }}>{f.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── ORDERS (split, screenshot right) ────────────────────────────── */}
      <SplitSection
        id="orders"
        screenshotSrc={`${S}orders.png?v=3`} screenshotAlt="Order Management" screenshotSide="right"
        label="ORDER MANAGEMENT" labelColor="#dc2626" labelBg="rgba(220,38,38,0.08)"
        title="Every Order. Tracked Automatically."
        body="Orders sync in from every sales channel automatically. Compare shipping rates across carriers, buy discounted labels in bulk, and track every delivery from one screen. No more switching between tabs."
        checks={['Auto-sync orders from every sales channel', 'Compare USPS, UPS & FedEx rates', 'Buy shipping labels at discounted rates', 'Auto-track deliveries & notify buyers', 'Revenue, profit & fee breakdown per order']}
        checkColor="#dc2626" checkBg="rgba(220,38,38,0.07)" checkBorder="rgba(220,38,38,0.2)"
      />

      {/* ── GRADING (split, custom PSA preview mockup on the right) ─────── */}
      <SplitSection
        id="grading"
        customVisual={<PsaPreviewMockup />} screenshotSide="right"
        label="GRADING WORKFLOW" labelColor="#2f5488" labelBg="rgba(124,58,237,0.08)"
        title="The Whole PSA Workflow, Done For You"
        body="Drop in your PSA submission PDF: every card is logged with service level, declared value, and grading fee, all auto-parsed. When PSA returns your receipt CSV, drop that in and every grade + cert number applies in one click. Raw cost auto-matches from your existing inventory so the eventual sale's P&L is actually right."
        checks={[
          'Drag-drop PSA submission PDF: every card logged in seconds',
          'PSA receipt CSV import: grades + cert numbers in one click',
          'Raw cost auto-matched from your existing inventory',
          'Grading fee auto-tracked as an expense per submission',
          '"Should I Grade This?" AI: photo → exact profit at PSA 10/9/8',
          'Cert lookup for PSA, BGS, SGC, and CGC',
        ]}
        checkColor="#2f5488" checkBg="rgba(124,58,237,0.07)" checkBorder="rgba(124,58,237,0.2)"
      />

      {/* ── COLLECTIONS (split, screenshot left) ────────────────────────── */}
      <SplitSection
        id="collections"
        screenshotSrc={`${S}collection.png`} screenshotAlt="Collections Portfolio" screenshotSide="left"
        label="COLLECTION MANAGEMENT" labelColor="#1c56cc" labelBg="rgba(29,78,216,0.08)"
        title={"Your Collection. What It's Actually Worth."}
        body="Separate your personal collection from inventory. Watch your portfolio value live with 180-day history. Scan a cert barcode and we pull the PSA photo, grade, and current market value automatically."
        checks={['180-day portfolio value chart', 'Live market prices via CardHedger', 'Card of the Day with current value', 'Set tracking & completion progress', 'PSA photo auto-pull on cert lookup']}
        checkColor="#1c56cc" checkBg="rgba(29,78,216,0.07)" checkBorder="rgba(29,78,216,0.2)"
      />

      {/* ── MORE FEATURES ───────────────────────────────────────────────── */}
      <section className="lp-section-pad" style={{ background: '#f5f6fa', padding: '72px 64px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 40, fontWeight: 800, color: '#0a0a1a', letterSpacing: '-1.2px', marginBottom: 10 }}>Everything in One Place</h2>
              <p style={{ fontSize: 16, color: '#666', maxWidth: 440, margin: '0 auto' }}>We got tired of juggling 6 different apps. So we built one that does all of it.</p>
            </div>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }} className="lp-grid-3">
            {FEATURES.map((f, i) => (
              <FadeIn key={f.title} delay={i * 0.06}>
                <div className="lp-feature-card">
                  <div style={{ width: 38, height: 38, borderRadius: 9, background: '#eef0ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1a36d5', marginBottom: 14 }}>
                    <FeatureIcon name={f.icon} />
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6, color: '#0a0a1a' }}>{f.title}</div>
                  <p style={{ color: '#666', fontSize: 13, lineHeight: 1.6, margin: 0 }}>{f.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ─────────────────────────────────────────────────────── */}
      <section id="pricing" className="lp-section-pad" style={{ background: '#f5f6fa', padding: '72px 64px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 22 }}>
              <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 42, fontWeight: 800, color: '#0a0a1a', letterSpacing: '-1.5px', marginBottom: 10 }}>Pricing That Makes Sense</h2>
              <p style={{ fontSize: 16, color: '#666', maxWidth: 440, margin: '0 auto' }}>Start your free trial. Cancel any time before it ends.</p>
            </div>
          </FadeIn>
          {/* Monthly / Annual toggle */}
          <FadeIn>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 13, marginBottom: 34, flexWrap: 'wrap' }}>
              <button onClick={() => setAnnual(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: annual ? '#9ca3af' : '#0a0a1a' }}>Monthly</button>
              <div onClick={() => setAnnual(a => !a)} style={{ position: 'relative', width: 54, height: 30, background: NAVY, borderRadius: 99, cursor: 'pointer' }}>
                <div style={{ position: 'absolute', top: 3, left: 3, width: 24, height: 24, background: 'white', borderRadius: '50%', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', transition: 'transform 0.22s cubic-bezier(.4,0,.2,1)', transform: annual ? 'translateX(24px)' : 'none' }} />
              </div>
              <button onClick={() => setAnnual(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: annual ? '#0a0a1a' : '#9ca3af' }}>Annual</button>
              <span style={{ background: '#dcfce7', color: '#166534', fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 99, letterSpacing: '0.02em' }}>2 months free</span>
            </div>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18, maxWidth: 920, margin: '0 auto' }} className="lp-grid-5">
            {PRICING.map((tier, i) => (
              <FadeIn key={tier.name} delay={i * 0.07}>
                <div className={`lp-pricing-card${tier.highlight ? ' highlight' : ''}`} style={{ position: 'relative' }}>
                  {tier.highlight && <div style={{ position: 'absolute', top: -11, left: '50%', transform: 'translateX(-50%)', background: GOLD, color: NAVY, fontSize: 10, fontWeight: 800, letterSpacing: '0.07em', padding: '4px 13px', borderRadius: 99, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Most Popular</div>}
                  <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 4, marginTop: tier.highlight ? 6 : 0, color: tier.highlight ? 'white' : '#0a0a1a' }}>{tier.name}</div>
                  <div style={{ marginBottom: 2, display: 'flex', alignItems: 'baseline', gap: 3 }}>
                    <span style={{ fontSize: 28, fontWeight: 800, color: tier.highlight ? GOLD : NAVY, letterSpacing: '-1px' }}>{tier.m === 0 ? '$0' : (annual ? `$${tier.a}` : `$${tier.m}`)}</span>
                    <span style={{ fontSize: 13, color: tier.highlight ? 'rgba(255,255,255,0.5)' : '#999' }}>{tier.m === 0 ? '' : (annual ? '/yr' : '/mo')}</span>
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: tier.highlight ? 'rgba(255,255,255,0.7)' : '#16a34a', marginBottom: 12, minHeight: 14, lineHeight: 1.3 }}>{annual && tier.m > 0 ? `$${(tier.a / 12).toFixed(2)}/mo billed yearly` : ' '}</div>
                  <div style={{ fontSize: 11, color: tier.highlight ? 'rgba(255,255,255,0.5)' : '#999', marginBottom: 18, lineHeight: 1.4 }}>{tier.cards}</div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 22 }}>
                    {tier.features.map(f => (
                      <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <span style={{ color: tier.highlight ? GOLD : NAVY, fontWeight: 700, flexShrink: 0, fontSize: 13 }}>✓</span>
                        <span style={{ fontSize: 12, color: tier.highlight ? 'rgba(255,255,255,0.75)' : '#444' }}>{f}</span>
                      </div>
                    ))}
                  </div>
                  <Link to="/register" style={{ display: 'block', textAlign: 'center', padding: '11px', borderRadius: 9, fontWeight: 700, fontSize: 14, textDecoration: 'none', background: tier.highlight ? GOLD : NAVY, color: tier.highlight ? NAVY : 'white', transition: 'opacity 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.88'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                    {tier.cta}
                  </Link>
                </div>
              </FadeIn>
            ))}
          </div>

          {/* Detailed feature comparison, full breakdown for serious shoppers */}
          <FadeIn>
            <div style={{ marginTop: 64 }}>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <h3 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 28, fontWeight: 700, color: '#0a0a1a', letterSpacing: '-0.5px', marginBottom: 6 }}>Compare in Detail</h3>
                <p style={{ fontSize: 14, color: '#666', margin: 0 }}>Every feature, every tier. Pick what you actually need.</p>
              </div>
              <div className="lp-compare-hint" style={{ display: 'none', textAlign: 'center', fontSize: 12, color: '#999', marginBottom: 8, fontWeight: 600 }}>← swipe to compare all tiers →</div>
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: 12, border: '1px solid #e8e9ee', background: 'white' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 720 }}>
                  <thead>
                    <tr style={{ background: '#f5f6fa' }}>
                      <th style={{ textAlign: 'left', padding: '14px 16px', fontWeight: 700, color: '#0a0a1a', minWidth: 220 }}>Feature</th>
                      {[
                        { name: 'Starter', price: '$19.99/mo', highlight: false },
                        { name: 'Dealer', price: '$59.99/mo', highlight: true },
                        { name: 'Business', price: '$299.99/mo', highlight: false },
                      ].map((t) => (
                        <th key={t.name} style={{ padding: '14px 12px', textAlign: 'center', fontWeight: 700, color: '#0a0a1a', background: t.highlight ? 'rgba(13,27,62,0.05)' : 'transparent', minWidth: 100 }}>
                          {t.highlight && <div style={{ fontSize: 9, color: GOLD, letterSpacing: '0.1em', marginBottom: 3 }}>MOST POPULAR</div>}
                          <div>{t.name}</div>
                          <div style={{ fontSize: 11, color: '#999', fontWeight: 600, marginTop: 2 }}>{t.price}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {COMPARISON_GROUPS.map((group) => (
                      <React.Fragment key={group.title}>
                        <tr>
                          <td colSpan={4} style={{ padding: '18px 16px 8px', fontSize: 11, fontWeight: 700, color: '#888', letterSpacing: '0.08em', textTransform: 'uppercase', background: '#fafbfc', borderTop: '1px solid #f0f1f5' }}>
                            {group.title}
                          </td>
                        </tr>
                        {group.rows.map((row) => (
                          <tr key={row.label} style={{ borderTop: '1px solid #f0f1f5' }}>
                            <td style={{ padding: '11px 16px', color: '#333' }}>{row.label}</td>
                            {row.values.map((v, vi) => (
                              <td key={vi} style={{ padding: '11px 12px', textAlign: 'center', color: v === true ? NAVY : (v === false ? '#ccc' : '#444'), fontWeight: v === true ? 700 : 500, background: vi === 1 ? 'rgba(13,27,62,0.025)' : 'transparent', fontSize: v === true || v === false ? 16 : 12 }}>
                                {v === true ? '✓' : v === false ? '-' : v}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── FAQ, objection handlers right at the decision point ────────── */}
      <section className="lp-section-pad" style={{ background: 'white', padding: '72px 64px', borderTop: '1px solid #e8e9ee' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 36 }}>
              <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 38, fontWeight: 800, color: NAVY, letterSpacing: '-1.2px', marginBottom: 10 }}>Questions, answered.</h2>
              <p style={{ fontSize: 15, color: '#666' }}>The stuff dealers ask before they start.</p>
            </div>
          </FadeIn>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {FAQS.map((f, i) => (
              <FadeIn key={f.q} delay={i * 0.05}>
                <div style={{ background: '#f8f9fc', border: '1px solid #e8e9ee', borderRadius: 12, padding: '18px 20px' }}>
                  <div style={{ fontWeight: 700, fontSize: 15.5, color: NAVY, marginBottom: 8 }}>{f.q}</div>
                  <p style={{ fontSize: 14, color: '#555', lineHeight: 1.65, margin: 0 }}>{f.a}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 28, fontSize: 13.5, color: '#888' }}>
            More questions? <a href="/faq" style={{ color: '#1a36d5', fontWeight: 600, textDecoration: 'none' }}>See the full FAQ →</a>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ────────────────────────────────────────────────── */}
      <section className="lp-section-pad" style={{ background: '#f5f6fa', padding: '72px 64px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 40, fontWeight: 800, color: '#0a0a1a', letterSpacing: '-1.2px', marginBottom: 8 }}>What Dealers Are Saying</h2>
              <p style={{ fontSize: 15, color: '#888' }}>Real feedback from real people who deal cards for a living.</p>
            </div>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }} className="lp-grid-3">
            {TESTIMONIALS.map((t, i) => (
              <FadeIn key={t.name} delay={i * 0.09}>
                <div className="lp-testimonial">
                  <div style={{ display: 'flex', gap: 2, marginBottom: 14 }}>
                    {[...Array(5)].map((_, si) => <span key={si} style={{ color: GOLD, fontSize: 15 }}>★</span>)}
                  </div>
                  <p style={{ fontSize: 15, lineHeight: 1.7, color: '#333', marginBottom: 20, fontStyle: 'italic' }}>"{t.quote}"</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: t.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 12 }}>{t.initials}</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{t.name}</div>
                      <div style={{ fontSize: 12, color: '#888' }}>{t.role}</div>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMING SOON ─────────────────────────────────────────────────── */}
      <section className="lp-section-pad" style={{ background: '#f5f6fa', padding: '36px 64px', borderTop: '1px solid #e8e9ee', borderBottom: '1px solid #e8e9ee' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#bbb', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 }}>More Integrations Coming Soon</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
            {COMING_SOON.map(p => (
              <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f5f6fa', border: '1px solid #eee', borderRadius: 100, padding: '7px 16px', fontSize: 13, fontWeight: 600, color: '#666' }}>
                {p}
                <span style={{ background: '#f0f0f0', color: '#aaa', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 100, letterSpacing: '0.04em' }}>SOON</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SIGNUP CTA ──────────────────────────────────────────────────── */}
      <section id="waitlist" className="lp-waitlist-section" style={{ background: NAVY, padding: '80px 64px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.025, backgroundImage: 'linear-gradient(rgba(255,255,255,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.2) 1px, transparent 1px)', backgroundSize: '48px 48px', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 580, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <FadeIn>
            <div style={{ display: 'inline-block', background: `rgba(201,162,39,0.12)`, color: GOLD, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', padding: '5px 14px', borderRadius: 100, marginBottom: 20, border: `1px solid rgba(201,162,39,0.25)` }}>FREE TO START</div>
            <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 44, fontWeight: 800, color: 'white', letterSpacing: '-1.5px', marginBottom: 14, lineHeight: 1.1 }}>Ready to Run Your Business Better?</h2>
            <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.45)', marginBottom: 32, lineHeight: 1.65 }}>Start your 14-day free trial. Track your whole inventory, cancel any time.</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link className="lp-btn" to="/register" style={{ fontSize: 18, padding: '18px 44px' }}>Start Free Trial →</Link>
              <Link to="/login" className="lp-btn-ghost" style={{ fontSize: 16, padding: '16px 36px' }}>Sign In</Link>
            </div>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)', marginTop: 16 }}>Upgrade anytime. Cancel anytime.</p>
          </FadeIn>
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────────── */}
      <footer className="lp-footer" style={{ background: '#080f1e', padding: '48px 64px 28px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div className="lp-footer-cols" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 36, gap: 32 }}>
            <div>
              <div style={{ marginBottom: 14 }}><Logo height={34} /></div>
              <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, maxWidth: 220, lineHeight: 1.6, marginBottom: 18 }}>The business platform for sports card dealers.</p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Link className="lp-btn" to="/register" style={{ padding: '10px 18px', fontSize: 13 }}>Start Free Trial</Link>
                <a href="https://calendly.com/zfinkel1/scn-dealer-demo" target="_blank" rel="noopener noreferrer" style={{ padding: '10px 16px', fontSize: 13, fontWeight: 700, borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.85)', textDecoration: 'none' }}>Book a Demo</a>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 56, flexWrap: 'wrap' }}>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>Product</div>
                {NAV_LINKS.map(l => <div key={l} style={{ marginBottom: 9 }}><a href={`#${l.toLowerCase()}`} onClick={e => { e.preventDefault(); const el = document.getElementById(l.toLowerCase()); if (el) el.scrollIntoView({ behavior: 'smooth' }); }} style={{ color: 'rgba(255,255,255,0.35)', fontSize: 13, textDecoration: 'none' }}>{l}</a></div>)}
              </div>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>Company</div>
                {[['Contact', '/contact'], ['FAQ', '/faq'], ['Terms', '/terms'], ['Privacy', '/privacy']].map(([l, h]) => (
                  <div key={l} style={{ marginBottom: 9 }}><Link to={h} style={{ color: 'rgba(255,255,255,0.35)', fontSize: 13, textDecoration: 'none' }}>{l}</Link></div>
                ))}
              </div>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>Guides</div>
                {[['Best Card Scanner', '/guides/best-scanner-for-sports-cards'], ['Card Dealer Software', '/guides/sports-card-dealer-software'], ['Card Scanner Apps', '/guides/best-sports-card-scanner-app'], ['How to Submit to PSA', '/guides/how-to-submit-cards-to-psa'], ['PSA Submission Tracking', '/guides/how-to-track-psa-submissions'], ['Card Dealer Accounting', '/guides/card-dealer-accounting'], ['Card Consignment', '/guides/consignment-for-card-dealers'], ['All Guides', '/guides']].map(([l, h]) => (
                  <div key={h} style={{ marginBottom: 9 }}><Link to={h} style={{ color: 'rgba(255,255,255,0.35)', fontSize: 13, textDecoration: 'none' }}>{l}</Link></div>
                ))}
              </div>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>Stay Updated</div>
                <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, marginBottom: 10, lineHeight: 1.5, maxWidth: 220 }}>Get product updates and tips for card dealers.</p>
                {nlMsg ? (
                  <div style={{ color: GOLD, fontSize: 13, fontWeight: 600 }}>{nlMsg}</div>
                ) : (
                  <form onSubmit={handleNewsletterSubmit} style={{ display: 'flex', gap: 6 }}>
                    <input type="email" value={nlEmail} onChange={e => setNlEmail(e.target.value)} placeholder="you@email.com" required style={{ padding: '8px 12px', fontSize: 13, borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', color: 'white', width: 160, outline: 'none' }} />
                    <button type="submit" disabled={nlLoading} style={{ padding: '8px 14px', fontSize: 12, fontWeight: 700, borderRadius: 6, border: 'none', background: GOLD, color: '#0d1b3e', cursor: 'pointer', whiteSpace: 'nowrap' }}>{nlLoading ? '...' : 'Subscribe'}</button>
                  </form>
                )}
              </div>
            </div>
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: 20, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: 12 }}>© 2026 SportsCardNetwork. All rights reserved.</div>
            <div style={{ color: 'rgba(255,255,255,0.28)', fontSize: 12 }}>Inventory, listings, and real profit: one flat fee. · support@sportscardnetwork.ai</div>
          </div>
        </div>
      </footer>

      {/* Hero "Watch overview" video modal, clip 1 (Welcome), hosted in /public/videos. */}
      {showVideo && (
        <div
          onClick={() => setShowVideo(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(7,15,36,0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, backdropFilter: 'blur(4px)' }}
        >
          <div onClick={e => e.stopPropagation()} style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,0.5)', background: '#000', maxWidth: '92vw', maxHeight: '88vh' }}>
            <button
              onClick={() => setShowVideo(false)}
              aria-label="Close video"
              style={{ position: 'absolute', top: 12, right: 12, zIndex: 2, width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 20, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >×</button>
            <video
              src="/videos/founder-overview.mp4"
              controls
              autoPlay
              playsInline
              style={{ display: 'block', maxHeight: '88vh', maxWidth: '92vw', width: 'auto', height: 'auto', background: '#000' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
